import { useQuery } from '@tanstack/react-query'
import { GroupedConsumptionData } from '@vae/server/src/interfaces/consumptionData.interface'
import dayjs from 'dayjs'
import React, { createContext, useContext, useMemo } from 'react'
import { backendCall } from '../modules/backendApi'
import { trpc } from '../modules/trpc'
import useUser from './hooks/useUser'
import { ObjectContext } from './objectContext/ObjectContext'
import { UserContext } from './UserContext'
interface AppContextType {
	consumptionData: {
		consideredMonth: ConsumptionData
		comparisonMonth: ConsumptionData
		consideredWeek: ConsumptionData
		comparisonWeek: ConsumptionData
		consideredDay: ConsumptionData
		comparisonDay: ConsumptionData
	}
	lastAvailableConsumptionData?: dayjs.Dayjs | null
	currentMonthInvoiceAccumulation: number | null
	currentMonthAvgPricePerConsumption: number | null
	consumptionLoading: boolean
	invoiceLoading: boolean
	consumptionQuerySuccess: boolean
	invoiceAccQuerySuccess: boolean
}

export const ConsumptionDataContext = createContext({} as AppContextType)

export interface ConsumptionData {
	kwh: number
	utcStartHour: string
	localTime: {
		year: number
		month: number
		day: number
		weekDay: number
	}
}

const initial = {
	kwh: 0,
	utcStartHour: '',
	localTime: {
		year: 0,
		month: 0,
		day: 0,
		weekDay: 0,
	},
}

const initialConsumptionData = {
	consideredMonth: initial,
	comparisonMonth: initial,
	consideredWeek: initial,
	comparisonWeek: initial,
	consideredDay: initial,
	comparisonDay: initial,
}

const ConsumptionDataContextProvider = (props: any) => {
	const { user } = useUser()

	const object = useContext(ObjectContext)
	const { isAnonymousUser } = useContext(UserContext)

	const {
		data: consumptionData,
		isLoading: consumptionLoading,
		isSuccess: consumptionQuerySuccess,
	} = useQuery({
		queryKey: ['consumptionContextData', object.id],
		queryFn: async () => getConsumptionData(),
		staleTime: 6 * 1000 * 60 * 60, // 6 hours
	})

	const lastAvailableConsumptionData = useMemo(() => {
		if (!consumptionData?.consideredDay.localTime.year) return null
		const {
			localTime: { year, month, day },
		} = consumptionData.consideredDay

		return dayjs()
			.year(year)
			.month(month - 1)
			.date(day)
	}, [consumptionData])

	const {
		data: currentMonthInvoiceAccumulation,
		isFetching: invoiceLoading,
		isSuccess: invoiceAccQuerySuccess,
	} = trpc.objects.getInvoiceAccumulation.useQuery(
		{
			objectId: object.id,
			from: lastAvailableConsumptionData?.startOf('month').format() ?? '',
			to: lastAvailableConsumptionData?.endOf('day').format() ?? '',
			isSpotMargin: object.price.isSpotMargin,
			price: object.price.value,
			groupBy: 'month',
		},
		{ enabled: Boolean(lastAvailableConsumptionData) && !isAnonymousUser }
	)

	const getConsumptionData = async () => {
		if (!object.synerallId && !object.useDatahub) return initialConsumptionData

		const today = dayjs()

		const { monthly, weekly, daily }: GroupedConsumptionData =
			(await backendCall.objects.getConsumption(
				today.subtract(2, 'month').startOf('month').format(),
				today.add(1, 'day').format(),
				object.id!,
				{
					grouping: 'all',
					useDatahub: object.useDatahub,
					meteringPointEAN: object.gsrn ?? undefined,
					personalId: user?.personalId,
					synerallObjectId: object.synerallId ?? undefined,
					synerallContractId: object.synerallContractId ?? undefined,
				}
			)) as GroupedConsumptionData

		const newConsideredMonth = monthly[monthly.length - 1]

		const newComparisonMonth = monthly[monthly.length - 2]

		//Weeks
		const consideredWeek = weekly[weekly.length - 1]
		const comparisonWeek = weekly[weekly.length - 2]
		//days
		const consideredDay = daily[daily.length - 1]
		const comparisonDay = daily[daily.length - 2]

		return {
			...initialConsumptionData,
			consideredMonth: newConsideredMonth ?? initial,
			consideredWeek: consideredWeek ?? initial,
			consideredDay: consideredDay ?? initial,
			comparisonMonth: newComparisonMonth ?? initial,
			comparisonWeek: comparisonWeek ?? initial,
			comparisonDay: comparisonDay ?? initial,
		}
	}

	return (
		<ConsumptionDataContext.Provider
			value={{
				consumptionData: consumptionData ?? initialConsumptionData,
				consumptionLoading,
				invoiceLoading,
				lastAvailableConsumptionData,
				currentMonthInvoiceAccumulation:
					(currentMonthInvoiceAccumulation ?? [{ invoiceAccumulationInEur: 0 }])[0]
						?.invoiceAccumulationInEur ?? 0,
				currentMonthAvgPricePerConsumption:
					(currentMonthInvoiceAccumulation ?? [{ avgPricePerConsumption: 0 }])[0]
						?.avgPricePerConsumption ?? 0,
				consumptionQuerySuccess,
				invoiceAccQuerySuccess: invoiceAccQuerySuccess || !lastAvailableConsumptionData,
			}}
		>
			{props.children}
		</ConsumptionDataContext.Provider>
	)
}

export default ConsumptionDataContextProvider
